import { ReactNode } from "react";
import {
  AssessmentDeliveryState,
  BatchCalibrationAction,
  ComplianceProgramCalibrationType,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";
import { pluralize } from "@helpers/string";

/**
 * Configuration for confirmation and success messages used in performance assessment reports.
 * Centralizes string generation logic that was previously scattered across components.
 */

interface ConfirmationStringsOptions {
  label: UseLabelType;
  targetName: string;
  isTargetManager: boolean;
  calibrationType?: ComplianceProgramCalibrationType;
}

interface BatchActionStringsOptions {
  label: UseLabelType;
  calibrationType?: ComplianceProgramCalibrationType;
  targetCount: number;
}

/**
 * Get confirmation message for changing delivery state
 */
export function getDeliveryStateChangeConfirmation(
  state: AssessmentDeliveryState,
  options: ConfirmationStringsOptions,
): ReactNode {
  const { label, targetName, isTargetManager } = options;

  switch (state) {
    case AssessmentDeliveryState.AwaitingAdminApproval:
      return `Proceeding will mark this delivery package as Not Approved. If the package has been delivered already, ${targetName} will no longer have access to it. Do you want to continue?`;

    case AssessmentDeliveryState.Draft:
      return `Proceeding will Approve this delivery package and allow the manager of ${targetName} to share it whenever they choose. No further responses will be gathered. Do you want to continue?`;

    case AssessmentDeliveryState.Delivered:
      return isTargetManager
        ? `Proceeding will share the content of the ${label("review")} with ${targetName}. Do you want to continue?`
        : `This functionality is typically only meant to be used by the Manager of the Subject for this ${label("review")}. Proceeding will share the content of the ${label("review")} with ${targetName}. Do you want to continue?`;

    default:
      return "Are you sure you want to continue?";
  }
}

/**
 * Get success message for changing delivery state
 */
export function getDeliveryStateChangeSuccess(
  state: AssessmentDeliveryState,
  label: (key: string, opts?: { capitalize?: boolean }) => string,
): string {
  const reviewLabel = label("review", { capitalize: true });

  switch (state) {
    case AssessmentDeliveryState.AwaitingAdminApproval:
      return `${reviewLabel} package marked as Not Approved`;

    case AssessmentDeliveryState.Draft:
      return `${reviewLabel} package Approved for Delivery`;

    case AssessmentDeliveryState.Delivered:
      return `${reviewLabel} package Delivered`;

    default:
      return "Status updated successfully";
  }
}

/**
 * Get confirmation message for calibrating a review
 */
export function getCalibrateConfirmation(
  options: Pick<ConfirmationStringsOptions, "label" | "calibrationType">,
): ReactNode {
  const { label, calibrationType } = options;

  if (calibrationType === ComplianceProgramCalibrationType.PreCalibration) {
    const reviewLabel = label("review", { capitalize: true });
    return `This will make the Downward ${reviewLabel} available to the manager. The manager is told it is ready if the employee has already submitted their self ${label("review")}. You will not be able to edit any ratings again until after the manager submits the Downward ${reviewLabel}. Are you sure you want to continue?`;
  }

  return "Proceeding will mark this delivery package as Calibrated. Managers will still not be able to share it until it is marked as Approved. Do you want to continue?";
}

/**
 * Get success message for calibrating a review
 */
export function getCalibrateSuccess(
  options: Pick<
    ConfirmationStringsOptions,
    "label" | "calibrationType" | "targetName"
  >,
): string {
  const { label, calibrationType, targetName } = options;

  if (calibrationType === ComplianceProgramCalibrationType.PreCalibration) {
    return `${targetName} marked as Calibrated`;
  }

  return `${label("review", { capitalize: true })} marked as Calibrated`;
}

/**
 * Get confirmation message for undo delivery
 */
export function getUndoDeliveryConfirmation(targetName: string): ReactNode {
  return `Proceeding will mark this delivery package as Not Approved. ${targetName} will no longer have access to it. Do you want to continue?`;
}

/**
 * Get success message for undo delivery
 */
export function getUndoDeliverySuccess(label: UseLabelType): string {
  return `${label("review", { capitalize: true })} package delivery undone`;
}

/**
 * Get confirmation message for sending back submission to manager (reopening)
 */
export function getReopenConfirmation(
  label: UseLabelType,
  reopenCount = 1,
): ReactNode {
  const these = reopenCount === 1 ? "this" : "these";
  return `Reopening ${these} ${label("review", { capitalize: true, pluralize: reopenCount !== 1 })} will allow the responder to edit their responses. Do you want to continue?`;
}

/**
 * Get success message for reopening
 */
export function getReopenSuccess(label: UseLabelType): string {
  return `${label("review", { capitalize: true })} reopened. The responder can now edit their responses.`;
}

/**
 * Get confirmation message for batch calibration actions
 */
export function getBatchCalibrationConfirmation(
  action: BatchCalibrationAction,
  options: BatchActionStringsOptions,
): ReactNode {
  const { label, calibrationType, targetCount } = options;
  const reviewLabel = label("review");
  const packages = pluralize("package", targetCount);

  if (action === BatchCalibrationAction.MarkAsCalibrated) {
    const preCalibrationNote =
      calibrationType === ComplianceProgramCalibrationType.PreCalibration
        ? `This will make the Downward ${label("review", { capitalize: true })} available to the manager. Each manager is told it is ready if their employee has already submitted their self ${reviewLabel}. You will not be able to edit any ratings again until after the manager submits the Downward ${label("review", { capitalize: true })}. `
        : "";

    return `This will mark ${targetCount} ${reviewLabel} ${packages} as Calibrated. ${preCalibrationNote}Are you sure you want to do this?`;
  }

  // For both approveCalibrated and approveAll
  return `This will mark ${targetCount} ${reviewLabel} ${packages} as Approved for Delivery. Are you sure you want to do this?`;
}

/**
 * Get success message for batch calibration actions
 */
export function getBatchCalibrationSuccess(
  action: BatchCalibrationAction,
  label: UseLabelType,
): string {
  const reviewLabel = label("review");

  if (action === BatchCalibrationAction.MarkAsCalibrated) {
    return `All ${reviewLabel} packages marked as Calibrated`;
  }

  return `All ${reviewLabel} packages Approved`;
}
